"use client";

import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, BarChart3, Mail, Megaphone, MessageSquare, Target } from "lucide-react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { BlastComposeCard, BlastResultsCard } from "./blast-visual";

const EASE = [0.16, 1, 0.3, 1] as const;

const CAPABILITIES = [
  { icon: Target, label: "Targeted segments" },
  { icon: MessageSquare, label: "Text" },
  { icon: Mail, label: "Email" },
  { icon: BarChart3, label: "Real-time metrics" },
];

// Keep the live results readable on the left, dissolving only where the crisp
// compose card pops over its right edge.
const RESULTS_FADE = {
  WebkitMaskImage: "linear-gradient(to right, black 0%, black 62%, transparent 100%)",
  maskImage: "linear-gradient(to right, black 0%, black 62%, transparent 100%)",
} as const;

function HeroCopy() {
  const reduceMotion = useReducedMotion();
  const item = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
  };
  const container = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } },
  };
  const containerProps = reduceMotion
    ? {}
    : { initial: "hidden" as const, animate: "visible" as const, variants: container };
  const iv = reduceMotion ? undefined : item;

  return (
    <motion.div
      className="flex max-w-xl flex-col items-center text-center xl:items-start xl:text-left"
      {...containerProps}
    >
      <motion.span
        variants={iv}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground"
      >
        <Megaphone className="size-4" />
        Blasts
      </motion.span>

      <motion.h1
        variants={iv}
        className="mt-4 text-balance text-4xl font-bold leading-[1.1] tracking-tight text-foreground sm:text-[2.75rem] lg:text-5xl"
      >
        Reach the right audience, right away
      </motion.h1>

      <motion.p
        variants={iv}
        className="mt-6 max-w-lg text-balance leading-relaxed text-muted-foreground sm:text-lg"
      >
        Send a targeted text or email to exactly the right segment, or your whole list, then watch
        it deliver and convert in real time.
      </motion.p>

      <motion.div variants={iv} className="mt-8 w-full sm:w-auto">
        <Button asChild size="xl" className="group w-full sm:w-auto">
          <Link href="/book-a-demo">
            Book a demo
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </Button>
      </motion.div>

      <motion.div
        variants={iv}
        className="mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 xl:justify-start"
      >
        {CAPABILITIES.map(({ icon: Icon, label }) => (
          <span
            key={label}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground"
          >
            <Icon className="size-3.5" />
            {label}
          </span>
        ))}
      </motion.div>
    </motion.div>
  );
}

function Stage() {
  const reduceMotion = useReducedMotion();
  const resultsAnim = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, x: -16 },
        animate: { opacity: 1, x: 0 },
        transition: { duration: 0.8, ease: EASE, delay: 0.3 },
      };
  const composeAnim = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, x: 24, scale: 0.98 },
        animate: { opacity: 1, x: 0, scale: 1 },
        transition: { duration: 0.8, ease: EASE, delay: 0.45 },
      };

  return (
    // The compose card sits in flow and defines the height (it's the taller of
    // the two); the live results card is layered behind it so the section always
    // contains the composition without overflowing.
    <div className="relative w-full">
      {/* Supporting live results — readable base, dissolving under the compose card */}
      <motion.div
        aria-hidden
        {...resultsAnim}
        className="absolute left-0 top-[12%] hidden w-[360px] xl:block"
        style={RESULTS_FADE}
      >
        <BlastResultsCard className="opacity-70" />
      </motion.div>

      {/* What you send — crisp, popped in front */}
      <motion.div
        {...composeAnim}
        className="relative z-10 mx-auto w-[320px] sm:w-[360px] xl:ml-auto xl:mr-0"
      >
        <BlastComposeCard />
      </motion.div>
    </div>
  );
}

export function BlastsHero() {
  return (
    <section className="relative overflow-x-clip border-b border-border bg-background pb-16 pt-28 sm:pb-20 lg:pt-36">
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-4 lg:px-8 xl:grid-cols-[minmax(0,1fr)_minmax(0,600px)] xl:gap-10">
        <HeroCopy />
        <Stage />
      </div>
    </section>
  );
}
